import type { FC } from "react";

import { isServerSide } from "../utils";

import type { Dictionary } from "./types";

type Interpolation = string | number | undefined;

export const replaceInterpolation = (value: string, interpolation: Interpolation[] | undefined): string => {
  if (!interpolation || interpolation.length === 0) {
    return value;
  }

  return value.replace(/\{(\d+)\}/gi, (_, index: string) => {
    return String(!interpolation || interpolation[Number(index)]) || "";
  });
};

/**
 * Get translation by key
 * @param key string - translation key
 * @param interpolation Array<string> - interpolations, replace
 * @param defaultValue? string - value that displays in development mode
 * @returns string
 */

type TranslationKeyComponentProps = {
  value: string;
  keyValue: string;
};

// TODO: Should that be in translation or in UI lib?
export const TranslationKeyComponent: FC<TranslationKeyComponentProps> = ({ value, keyValue }) => (
  <>
    <span style={{ display: "none" }} className={"translation-key"}>
      {keyValue}
    </span>
    {value}
  </>
);

export const getTranslationValue =
  (dictionary: Dictionary) => (key: string, interpolation?: Interpolation[], defaultValue?: string) => {
    if (dictionary[key] || dictionary[key] === "") {
      return replaceInterpolation(dictionary[key], interpolation);
    }

    console.warn(`[Translation] Missing key: "${key}"`);

    return defaultValue ?? key;
  };

export const translateBackendErrorCode = <T extends string | undefined>(
  errorCode?: string,
  defaultErrorMessage?: T,
  dictionary: Dictionary = {}
) => {
  if (isServerSide()) {
    return defaultErrorMessage as T;
  }

  if (!errorCode || !dictionary[errorCode]) {
    return defaultErrorMessage as T;
  }

  return dictionary[errorCode];
};

export const buildErrorDictionary = <T extends Record<string, string | undefined>>(
  messages: T,
  codeMap: Record<keyof T & string, string>
): Dictionary =>
  Object.fromEntries(
    Object.entries(codeMap).flatMap(([key, errorCode]) => {
      const message = messages[key];
      return message ? [[errorCode, message]] : [];
    })
  );

export type ApiErrorWithErrorCode = {
  error_code?: string;
  message?: string;
  attributes?: Record<string, unknown>;
};

export const isApiErrorWithErrorCode = (error: unknown): error is ApiErrorWithErrorCode =>
  typeof error === "object" && error !== null && "error_code" in error;

export const VALIDATION_ERROR_CODE = "validation_error";

export const recursivelyConcatenateObjectKeys = (
  obj: Record<string, unknown>,
  defaultPrefix: string = ""
): string[] => {
  const result: string[] = [];

  const traverse = (current: Record<string, unknown>, prefix: string) => {
    Object.entries(current).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        traverse(value as Record<string, unknown>, newKey);
      } else {
        result.push(newKey);
      }
    });
  };

  traverse(obj, defaultPrefix);
  return result;
};

export const getErrorMessageFromError = (error: unknown, fallbackMessage = "", dictionary: Dictionary = {}): string => {
  if (typeof error !== "object" || error === null) {
    return fallbackMessage;
  }

  const typedError = error as ApiErrorWithErrorCode;

  if ("error_code" in typedError && typeof typedError.error_code !== "undefined") {
    if (typedError.error_code === VALIDATION_ERROR_CODE && typedError?.attributes) {
      const errorsArrays = recursivelyConcatenateObjectKeys(typedError.attributes, VALIDATION_ERROR_CODE);
      const translatedErrors = errorsArrays.map((errorKey) =>
        translateBackendErrorCode(errorKey, fallbackMessage, dictionary)
      );
      const uniqueTranslatedErrors = Array.from(new Set(translatedErrors));
      return uniqueTranslatedErrors.join(", ");
    }
    return translateBackendErrorCode(typedError.error_code, fallbackMessage, dictionary);
  } else if ("message" in typedError && typeof typedError.message !== "undefined") {
    return typedError.message;
  }

  return fallbackMessage;
};
